// chunkQueue.js — durable upload queue for v2 session recording.
//
// Receives timesliced webm blobs from MediaRecorder.ondataavailable, persists
// them to IndexedDB, and drains them to POST /api/session/{sid}/chunk in a
// single-flight worker with exponential backoff. If the tab crashes mid-session,
// pending chunks survive the restart (durability, not just retry).
//
// Public API:
//   const q = createChunkQueue({ sid });
//   q.enqueue(seq, blob)     → Promise<void>, resolves after IndexedDB write
//   await q.flush()          → resolves when every enqueued chunk is ACKed
//   q.pause() / q.resume()   → stop/restart the worker (e.g. when offline)
//   q.status                 → { pending, inflight, lastAcked, paused, lastError }
//
// The queue is intentionally minimal: no chunk validation, no merging, no mime
// sniffing. Server-side POST /chunk is the source of truth for ordering — the
// queue just guarantees every blob gets delivered at least once.

const DB_NAME = "didactic_session_chunks";
const STORE = "chunks";
const DB_VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        // Composite key [sid, seq] so we can list + range-query per session.
        db.createObjectStore(STORE, { keyPath: ["sid", "seq"] });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbPut(db, record) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function dbDelete(db, sid, seq) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete([sid, seq]);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function dbListForSid(db, sid) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const range = IDBKeyRange.bound([sid, 0], [sid, Number.MAX_SAFE_INTEGER]);
    const req = tx.objectStore(STORE).getAll(range);
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

const DEFAULTS = {
  postUrl: (sid) => `/api/session/${sid}/chunk`,
  maxAttempts: 6,
  baseBackoffMs: 250,    // 250, 500, 1000, 2000, 4000, 4000
  maxBackoffMs: 4000,
};

// ─── public factory ─────────────────────────────────────────────────────────

export function createChunkQueue({ sid, fetchFn, ...opts } = {}) {
  if (!sid) throw new Error("chunkQueue: sid required");
  const cfg = { ...DEFAULTS, ...opts };
  const fetcher = fetchFn || fetch;

  const state = {
    pending: 0,
    inflight: 0,
    lastAcked: -1,
    paused: false,
    lastError: null,
  };
  let dbPromise = openDb();
  let workerRunning = false;
  // Flush resolvers are notified when the queue drains (pending===0 + inflight===0).
  const flushResolvers = [];

  function notifyDrained() {
    if (state.pending === 0 && state.inflight === 0) {
      while (flushResolvers.length) flushResolvers.shift()();
    }
  }

  async function uploadOne(record) {
    const fd = new FormData();
    fd.append("seq", String(record.seq));
    fd.append("file", record.blob, `${record.seq}.webm`);
    const url = cfg.postUrl(sid);
    const res = await fetcher(url, { method: "POST", body: fd });
    if (!res.ok) {
      throw new Error(`chunk upload ${res.status}: ${await res.text().catch(() => "")}`);
    }
    const body = await res.json();
    return body.received_through;
  }

  async function drainOnce() {
    if (state.paused || workerRunning) return;
    workerRunning = true;
    try {
      const db = await dbPromise;
      while (!state.paused) {
        const queued = await dbListForSid(db, sid);
        if (queued.length === 0) break;
        queued.sort((a, b) => a.seq - b.seq);
        const record = queued[0];
        state.inflight = 1;
        let attempt = 0;
        let lastErr = null;
        while (attempt < cfg.maxAttempts) {
          try {
            const ack = await uploadOne(record);
            if (typeof ack === "number") state.lastAcked = Math.max(state.lastAcked, ack);
            await dbDelete(db, sid, record.seq);
            state.pending = Math.max(0, state.pending - 1);
            state.lastError = null;
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            attempt += 1;
            const delay = Math.min(cfg.maxBackoffMs, cfg.baseBackoffMs * 2 ** (attempt - 1));
            await new Promise((r) => setTimeout(r, delay));
          }
        }
        state.inflight = 0;
        if (lastErr) {
          state.paused = true;
          state.lastError = String(lastErr);
          break;
        }
      }
    } finally {
      workerRunning = false;
      notifyDrained();
    }
  }

  async function enqueue(seq, blob) {
    if (!(blob instanceof Blob) || blob.size === 0) return;
    const db = await dbPromise;
    await dbPut(db, { sid, seq, blob, queued_at: Date.now() });
    state.pending += 1;
    // Fire & forget — drain is idempotent under single-flight guard.
    drainOnce();
  }

  async function flush() {
    // Kick the worker in case it was idle, then await drain.
    drainOnce();
    if (state.pending === 0 && state.inflight === 0) return;
    await new Promise((resolve) => flushResolvers.push(resolve));
  }

  function pause() { state.paused = true; }
  function resume() {
    state.paused = false;
    state.lastError = null;
    drainOnce();
  }

  // If the page was reloaded mid-session, any previously-queued chunks for
  // this sid are re-picked-up on construction.
  drainOnce();

  return {
    enqueue, flush, pause, resume,
    get status() { return { ...state }; },
    // Test seam — overridden by unit tests.
    _drainOnce: drainOnce,
  };
}
